/**
 * Reusable toggle function that handles toggle state and returns current value
 * @param {string} toggleId - ID of the toggle container element
 * @param {string} optionSelector - CSS selector for toggle options (e.g., '.toggle-option, .pill-option')
 * @param {string} dataAttribute - Data attribute name that contains the toggle value (e.g., 'data-type')
 * @param {string} defaultValue - Default value when no option is selected
 * @returns {Object} - Object with getCurrentValue(), toggleValue(), setValue(), onChange(), and removeAllListeners() methods
 */

export function initializeToggle(toggleId, optionSelector = '.toggle-option, .pill-option', dataAttribute = 'data-type', defaultValue = null) {

    const toggle = document.getElementById(toggleId);
    if (!toggle) {
        console.warn(`Toggle element with ID '${toggleId}' not found`);
        return null;
    }

    let currentValue = defaultValue;
    const changeListeners = [];

    // Set initial value if no default is provided
    if (currentValue === null) {
        const activeOption = toggle.querySelector(`${optionSelector}.active`);
        if (activeOption) {
            currentValue = activeOption.getAttribute(dataAttribute);
        }
    }

    // Helper function to trigger change event
    const triggerChangeEvent = (oldValue, newValue) => {
        changeListeners.forEach(listener => {
            try {
                listener(newValue, oldValue);
            } catch (error) {
                console.error('Error in toggle change listener:', error);
            }
        });
    };

    // Handle toggle clicks
    toggle.addEventListener('click', function(e) {
        if (e.target.matches(optionSelector)) {
            const oldValue = currentValue;

            // Remove active class from all options
            const options = toggle.querySelectorAll(optionSelector);
            options.forEach(option => option.classList.remove('active'));

            // Add active class to clicked option
            e.target.classList.add('active');

            // Update current value
            currentValue = e.target.getAttribute(dataAttribute);

            // Trigger change event if value actually changed
            if (oldValue !== currentValue) {
                triggerChangeEvent(oldValue, currentValue);
            }
        }
    });

        // Return API for external use
    return {
        /**
         * Get the current toggle value
         * @returns {string|null} - Current selected value
         */
        getCurrentValue: () => currentValue,

        /**
         * Set the toggle value programmatically
         * @param {string} value - Value to set
         * @returns {boolean} - Success status
         */
        setValue: (value) => {
            const oldValue = currentValue;
            const targetOption = toggle.querySelector(`${optionSelector}[${dataAttribute}="${value}"]`);
            if (targetOption) {
                // Remove active class from all options
                const options = toggle.querySelectorAll(optionSelector);
                options.forEach(option => option.classList.remove('active'));

                // Add active class to target option
                targetOption.classList.add('active');
                currentValue = value;

                // Trigger change event if value actually changed
                if (oldValue !== currentValue) {
                    triggerChangeEvent(oldValue, currentValue);
                }

                return true;
            }
            return false;
        },

        /**
         * Toggle to the next available option
         * @returns {string|null} - The new value after toggling, or null if toggle failed
         */
        toggleValue: () => {
            const options = toggle.querySelectorAll(optionSelector);
            if (options.length < 2) {
                console.warn('Toggle needs at least 2 options to toggle between');
                return null;
            }

            // Find all available values
            const availableValues = Array.from(options).map(option =>
                option.getAttribute(dataAttribute)
            ).filter(value => value !== null);

            if (availableValues.length < 2) {
                console.warn('Toggle needs at least 2 valid values to toggle between');
                return null;
            }

            // Find the next value (cycle through available options)
            const currentIndex = availableValues.indexOf(currentValue);
            const nextIndex = (currentIndex + 1) % availableValues.length;
            const nextValue = availableValues[nextIndex];

            // Set the next value
            const success = this.setValue(nextValue);
            return success ? nextValue : null;
        },

        /**
         * Add a change event listener
         * @param {Function} listener - Function to call when value changes (newValue, oldValue) => {}
         * @returns {Function} - Function to remove this specific listener
         */
        onChange: (listener) => {
            if (typeof listener === 'function') {
                changeListeners.push(listener);
                // Return a function to remove this specific listener
                return () => {
                    const index = changeListeners.indexOf(listener);
                    if (index > -1) {
                        changeListeners.splice(index, 1);
                    }
                };
            }
            return () => {};
        },

        /**
         * Remove all change event listeners
         */
        removeAllListeners: () => {
            changeListeners.length = 0;
        },

        /**
         * Get the toggle element
         * @returns {HTMLElement} - The toggle container element
         */
        getElement: () => toggle
    };
}